import React, { useState, useEffect, useCallback } from 'react';
import { Navigation, RefreshCw, XCircle } from 'lucide-react';
import apiService from '../apiService';
import { useAdmin } from '../context/AdminContext';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

// Statuts « actifs » = de la recherche à la course en cours (même liste que le compteur du dashboard).
const ACTIVE_STATUSES = ['searching', 'queued', 'accepted', 'driver_arriving', 'driver_arrived', 'in_progress'];
const STATUS_META = {
  searching: { label: 'En recherche', color: '#FFB900' },
  queued: { label: 'En file', color: '#FFB900' },
  accepted: { label: 'Acceptée', color: '#FF7800' },
  driver_arriving: { label: 'Chauffeur en route', color: '#FF7800' },
  driver_arrived: { label: 'Chauffeur arrivé', color: '#FF7800' },
  in_progress: { label: 'En cours', color: '#10B981' },
};
const TYPE_LABELS = { mbiyo: 'Mbiyo', confort: 'Confort', premium: 'Premium', covoiturage: 'Covoiturage', moto: 'Moto' };
const typeLabel = (t) => TYPE_LABELS[t] || t || '—';
const REFRESH_MS = 15000;

// « il y a 3 min » depuis une date.
const sinceLabel = (d) => {
  if (!d) return '—';
  const min = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
};

export default function ActiveRidesPage() {
  const { setCurrentPage } = useAdmin();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const cities = useCities();
  // Annulation par l'admin (2026-10-30) : motif obligatoire ; frais au client seulement si coché.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [chargeClient, setChargeClient] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiService.get(`/taxi/admin/rides?status=${ACTIVE_STATUSES.join(',')}&limit=200`, { userType: 'admin' });
      if (res?.success) { setRides(res.rides || []); setUpdatedAt(new Date()); }
    } catch (err) { console.error('Erreur courses actives:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const openCancel = (ride) => { setCancelTarget(ride); setCancelReason(''); setChargeClient(false); setCancelError(null); };
  const submitCancel = async () => {
    if (!cancelTarget) return;
    if (cancelReason.trim().length < 3) { setCancelError('Indiquez le motif (il sera envoyé au client et au chauffeur).'); return; }
    setCancelling(true); setCancelError(null);
    try {
      const res = await apiService.delete(`/taxi/${cancelTarget.ride_id}/cancel`, {
        userType: 'admin', body: JSON.stringify({ reason: cancelReason.trim(), charge_client: chargeClient }),
      });
      if (!res?.success) throw new Error(res?.error || 'Annulation impossible');
      setCancelTarget(null);
      load();
    } catch (e) { setCancelError(e?.error || e?.message || 'Annulation impossible'); }
    finally { setCancelling(false); }
  };

  const visible = rides
    .filter(r => !statusFilter || r.status === statusFilter)
    .filter(r => !cityFilter || r.city_slug === cityFilter);
  const countBy = (s) => rides.filter(r => r.status === s && (!cityFilter || r.city_slug === cityFilter)).length;

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Navigation size={28} /> Courses actives <span className="dash-live-dot" />
        </h1>
        <div className="page-actions">
          <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} />
          <button className="btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshCw size={16} /> Actualiser
          </button>
        </div>
      </div>
      <p style={{ margin: '-0.5rem 0 1rem', fontSize: '0.8rem', color: '#9AA0A8' }}>
        Rafraîchi toutes les 15 s{updatedAt ? ` · dernière mise à jour ${updatedAt.toLocaleTimeString('fr-FR')}` : ''} ·{' '}
        <span onClick={() => setCurrentPage('taxis')} style={{ color: '#C9350F', cursor: 'pointer', fontWeight: 600 }}>Voir toutes les courses →</span>
      </p>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button onClick={() => setStatusFilter('')}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', background: !statusFilter ? '#F54119' : '#F4F5F7', color: !statusFilter ? 'white' : '#5C636E' }}>
          Toutes ({cityFilter ? rides.filter(r => r.city_slug === cityFilter).length : rides.length})
        </button>
        {ACTIVE_STATUSES.map(key => (
          <button key={key} onClick={() => setStatusFilter(key)}
            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem', background: statusFilter === key ? STATUS_META[key].color : '#F4F5F7', color: statusFilter === key ? 'white' : '#5C636E' }}>
            {STATUS_META[key].label} ({countBy(key)})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#FFFFFF' }}>
                  {['ID', 'Client', 'Chauffeur', 'Type', 'Trajet', 'Prix', 'Statut', 'Ville', 'Depuis', ''].map((h, i) => (
                    <th key={i} style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#5C636E' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucune course active{cityFilter ? ` à ${cityLabel(cities, cityFilter)}` : ''} en ce moment</td></tr>
                )}
                {visible.map(ride => {
                  const meta = STATUS_META[ride.status] || { label: ride.status, color: '#5C636E' };
                  return (
                    <tr key={ride.ride_id} style={{ borderTop: '1px solid #F4F5F7' }}>
                      <td style={{ padding: '12px' }}><span style={{ fontSize: '0.75rem', color: '#5C636E', fontFamily: 'monospace' }}>{ride.ride_id?.slice(-8)}</span></td>
                      <td style={{ padding: '12px', fontWeight: '500' }}>{ride.client_name || '—'}</td>
                      <td style={{ padding: '12px', fontWeight: '500', color: ride.driver_name ? '#1E2229' : '#9AA0A8' }}>{ride.driver_name || 'Pas encore attribuée'}</td>
                      <td style={{ padding: '12px', fontSize: '0.8rem' }}>
                        {Array.isArray(ride.ride_types) && ride.ride_types.length > 1
                          ? <span>{ride.ride_types.map(typeLabel).join(' + ')}{ride.driver_id ? <span> → <strong>{typeLabel(ride.ride_type)}</strong></span> : null}</span>
                          : typeLabel(ride.ride_type)}
                      </td>
                      <td style={{ padding: '12px', maxWidth: '220px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#414852', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ride.pickup?.address}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>→ {ride.dropoff?.address}</div>
                      </td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#C9350F' }}>{(ride.final_price || ride.estimated_price || 0).toLocaleString('fr-FR')} CDF</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: `${meta.color}20`, color: meta.color, whiteSpace: 'nowrap' }}>{meta.label}</span>
                      </td>
                      <td style={{ padding: '12px', color: '#5C636E' }}>{cityLabel(cities, ride.city_slug)}</td>
                      <td style={{ padding: '12px', fontSize: '0.8rem', color: '#5C636E', whiteSpace: 'nowrap' }}>{sinceLabel(ride.requested_at || ride.created_at)}</td>
                      <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                        {ride.status !== 'in_progress' && (
                          <button type="button" onClick={() => openCancel(ride)} title="Annuler cette course (le client et le chauffeur sont prévenus)"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '8px', border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                            <XCircle size={14} /> Annuler
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div className="modal-overlay" onClick={() => !cancelling && setCancelTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <h3 style={{ margin: '0 0 6px' }}>Annuler la course {cancelTarget.ride_id?.slice(-8)}</h3>
            <p style={{ margin: '0 0 14px', color: '#5C636E', fontSize: '0.85rem' }}>
              {cancelTarget.client_name || 'Client'}{cancelTarget.driver_name ? ` · ${cancelTarget.driver_name}` : ' · pas encore attribuée'} · {STATUS_META[cancelTarget.status]?.label || cancelTarget.status}.
              Le client et le chauffeur recevront « annulée par Mbiyo » avec votre motif.
            </p>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: '6px' }}>Motif (obligatoire)</label>
            <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} maxLength={300} autoFocus
              placeholder="Ex. : chauffeur injoignable, demande du client au support, doublon…"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #DCE0E6', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '12px 0 4px', fontSize: '0.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={chargeClient} onChange={(e) => setChargeClient(e.target.checked)} style={{ marginTop: '2px' }} />
              <span>Facturer les frais d'annulation au client (selon la phase de la course). Décoché : aucun frais, aucune pénalité de note, remboursement intégral si la course était payée.</span>
            </label>
            {cancelError && <p style={{ color: '#DC2626', fontSize: '0.85rem', margin: '8px 0 0' }}>{cancelError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button type="button" className="btn-secondary" disabled={cancelling} onClick={() => setCancelTarget(null)}>Retour</button>
              <button type="button" disabled={cancelling} onClick={submitCancel}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#DC2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {cancelling ? 'Annulation…' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
